/**
 * Which tab owns a stored recording.
 *
 * Two tabs of the same app share one IndexedDB, so without this the second one would find the
 * first one's *running* recording on disk and offer to send it — a half-finished tutorial shipped
 * out from under its author.
 *
 * A Web Lock is the browser's own liveness signal rather than one we maintain: it is released the
 * instant the holding document dies, whether by reload, close, crash or the operating system, with
 * no heartbeat to keep warm and no staleness window to guess at. That matters here more than
 * usual, because a paused recording emits no chunks at all — any heartbeat we drove off the data
 * we write would go quiet exactly when the recording is most alive.
 */

const held = new Map<string, () => void>();

function lockName(videoId: string) {
	return `msuite-recording-${videoId}`;
}

/**
 * Whether this browser can tell a running recording from an abandoned one.
 *
 * @returns True when the Web Locks API is present.
 */
export function canClaimRecordings() {
	return typeof navigator !== "undefined" && !!navigator.locks;
}

/**
 * Take ownership of a recording for as long as this document lives.
 *
 * @param videoId - The recording to claim.
 * @returns True when this document now owns it, false when another one does.
 */
export function claimRecording(videoId: string) {
	if (!canClaimRecordings()) return Promise.resolve(false);
	if (held.has(videoId)) return Promise.resolve(true);

	return new Promise<boolean>((settle) => {
		void navigator.locks
			.request(lockName(videoId), { ifAvailable: true }, (lock) => {
				if (!lock) {
					settle(false);
					return;
				}
				// The lock lasts as long as this promise does, and nothing settles it but
				// `releaseRecording` or this document ending.
				return new Promise<void>((release) => {
					held.set(videoId, () => {
						held.delete(videoId);
						release();
					});
					settle(true);
				});
			})
			.catch(() => settle(false));
	});
}

/** Give up a recording, so another tab may take it. A no-op for one this document does not hold. */
export function releaseRecording(videoId: string) {
	held.get(videoId)?.();
}
